/**
 * Validation helper utilities for discipline service
 */

/**
 * Validate mention data before creation
 * @param {Object} data - Mention data to validate
 * @returns {Object} Validation result with isValid flag and error message
 */
export function validateMentionData(data) {
  if (!data.studentId || !data.studentName) {
    return {
      isValid: false,
      error: 'Student information is required'
    };
  }

  if (!data.offence && !data.reason && !data.offense) {
    return {
      isValid: false,
      error: 'Offence is required'
    };
  }

  if (!data.issuedById) {
    return {
      isValid: false,
      error: 'Issuer information is required'
    };
  }

  return { isValid: true };
}

/**
 * Validate offence data before creation
 * @param {Object} data - Offence data to validate
 * @returns {Object} Validation result with isValid flag and error message
 */
export function validateOffenceData(data) {
  if (!data.name || data.name.trim() === '') {
    return {
      isValid: false,
      error: 'Offence name is required'
    };
  }

  // Category is optional - offences can be uncategorized

  // Levels validation is optional - form handles multiple levels differently
  // Only validate if a single level is provided
  if (data.level !== undefined) {
    const validLevels = [1, 2, 3, 4, 5];
    if (!validLevels.includes(data.level)) {
      return {
        isValid: false,
        error: 'Level must be between 1 and 5'
      };
    }
  }

  return { isValid: true };
}

/**
 * Validate discipline rule data
 * @param {Object} data - Rule data to validate
 * @returns {Object} Validation result with isValid flag and error message
 */
export function validateRuleData(data) {
  if (!data.name || data.name.trim() === '') {
    return {
      isValid: false,
      error: 'Rule name is required'
    };
  }

  if (!data.type) {
    return {
      isValid: false,
      error: 'Rule type is required'
    };
  }

  // Validate mention rules
  if (data.type === 'mention') {
    if (!data.mentionThreshold || data.mentionThreshold < 1) {
      return {
        isValid: false,
        error: 'Mention threshold must be at least 1'
      };
    }

    const validScopes = ['same-offense', 'same-category', 'same-lesson', 'any'];
    if (!validScopes.includes(data.mentionScope)) {
      return {
        isValid: false,
        error: 'Invalid mention scope'
      };
    }

    if (!data.timeframe || typeof data.timeframe !== 'string') {
      return {
        isValid: false,
        error: 'Invalid timeframe'
      };
    }

    if (!data.timeCount || data.timeCount < 1) {
      return {
        isValid: false,
        error: 'Time count must be at least 1'
      };
    }
  }

  // Validate accountability rules
  if (data.type === 'accountability') {
    if (!data.thresholds || data.thresholds.length === 0) {
      return {
        isValid: false,
        error: 'At least one threshold is required for accountability rules'
      };
    }

    for (const threshold of data.thresholds) {
      if (!threshold.demerits || threshold.demerits < 1) {
        return {
          isValid: false,
          error: 'Demerit threshold must be at least 1'
        };
      }

      const validConsequences = ['detention', 'saturday-detention', 'suspension'];
      if (!validConsequences.includes(threshold.consequenceType)) {
        return {
          isValid: false,
          error: 'Invalid consequence type for threshold'
        };
      }

      if (!threshold.timeframe || typeof threshold.timeframe !== 'string') {
        return {
          isValid: false,
          error: 'Invalid timeframe for threshold'
        };
      }
    }
  }

  return { isValid: true };
}

/**
 * Validate discipline consequence data
 * @param {Object} data - Consequence data to validate
 * @returns {Object} Validation result with isValid flag and error message
 */
export function validateConsequenceData(data) {
  if (!data.studentId) {
    return {
      isValid: false,
      error: 'Student ID is required'
    };
  }

  if (!data.studentName) {
    return {
      isValid: false,
      error: 'Student name is required'
    };
  }

  if (!data.type) {
    return {
      isValid: false,
      error: 'Consequence type is required'
    };
  }

  const validTypes = ['demerit', 'detention', 'suspension', 'saturday-detention', 'warning'];
  if (!validTypes.includes(data.type)) {
    return {
      isValid: false,
      error: `Invalid type: ${data.type}. Valid types: ${validTypes.join(', ')}`
    };
  }

  // Type-specific validation
  if (data.type === 'demerit' && (!data.points || data.points < 1)) {
    return {
      isValid: false,
      error: 'Demerit points must be at least 1'
    };
  }

  if ((data.type === 'detention' || data.type === 'saturday-detention') && !data.scheduledDate) {
    return {
      isValid: false,
      error: 'Scheduled date is required for detentions'
    };
  }

  if (data.type === 'suspension' && (!data.startDate || !data.endDate)) {
    return {
      isValid: false,
      error: 'Start and end dates are required for suspensions'
    };
  }

  return { isValid: true };
}